<template>
  <v-card outlined class="feedback-chat-panel">
    <v-card-title class="text-subtitle-1 font-weight-bold">
      Comments
    </v-card-title>

    <v-card-text class="messages-container" ref="messagesContainer">
      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-else-if="messages.length === 0" class="text-center py-8 grey--text">
        No messages yet. Start the conversation!
      </div>

      <div v-else class="messages-list">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="['message-item', message.senderRole === 'staff' ? 'message-sent' : 'message-received']"
        >
          <!-- Family Message (Left) -->
          <v-row v-if="message.senderRole === 'family'" class="mx-0">
            <div class="d-flex align-start" style="max-width: 75%;">
              <v-avatar size="40" class="mr-3 flex-shrink-0" color="grey lighten-2" style="margin-top: 5px;">
                <v-img v-if="message.senderPhotoUrl" :src="message.senderPhotoUrl"></v-img>
                <v-icon v-else color="grey darken-1" size="32">mdi-account-circle</v-icon>
              </v-avatar>
              <v-card color="grey lighten-4" class="message-card-received">
                <v-card-text class="pa-3">
                  <div class="text-caption grey--text mb-1">{{ message.senderName }}</div>
                  <div v-if="message.text" class="text-body-1 message-text">{{ message.text }}</div>
                  <div v-if="message.medias && message.medias.length" class="mt-2">
                    <v-img
                      v-for="(media, i) in message.medias"
                      :key="i"
                      :src="media.thumbnailUrl || media.url"
                      max-width="250"
                      class="mb-2 rounded cursor-pointer"
                      @click="viewFullImage(media.url)"
                    ></v-img>
                  </div>
                  <div class="text-caption grey--text text-right mt-1">
                    {{ message.createdAtDisplay }}
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-row>

          <!-- Staff Message (Right) -->
          <v-row v-else class="mx-0 justify-end">
            <div class="d-flex align-start" style="max-width: 75%;">
              <v-card color="primary" class="message-card-sent">
                <v-card-text class="pa-3 white--text">
                  <div class="text-caption mb-1">{{ message.senderName }}</div>
                  <div v-if="message.text" class="text-body-1 message-text">{{ message.text }}</div>
                  <div v-if="message.medias && message.medias.length" class="mt-2">
                    <v-img
                      v-for="(media, i) in message.medias"
                      :key="i"
                      :src="media.thumbnailUrl || media.url"
                      max-width="250"
                      class="mb-2 rounded cursor-pointer"
                      @click="viewFullImage(media.url)"
                    ></v-img>
                  </div>
                  <div class="text-caption text-right mt-1">
                    {{ message.createdAtDisplay }}
                  </div>
                </v-card-text>
              </v-card>
              <v-avatar size="40" class="ml-3 flex-shrink-0" color="white">
                <v-img v-if="message.senderPhotoUrl" :src="message.senderPhotoUrl"></v-img>
                <v-icon v-else color="primary" size="32">mdi-account</v-icon>
              </v-avatar>
            </div>
          </v-row>
        </div>
        <div ref="bottomElement"></div>
      </div>
    </v-card-text>

    <!-- Message Input -->
    <v-card-actions v-if="!isReadOnly" class="pa-4 pt-0">
      <div class="flex-grow-1">
        <v-textarea
          v-model="newMessage"
          :disabled="sending || uploading"
          placeholder="Write a message..."
          rows="2"
          outlined
          dense
          hide-details
          @keydown.enter.exact.prevent="sendMessage"
        ></v-textarea>

        <div class="d-flex justify-space-between align-center mt-2">
          <div>
            <FeedbackMediaUploader
              :filePath="attachmentPath"
              :disabled="sending || uploading"
              @onFileStartUpload="uploading = true"
              @onFileUploaded="handleFileUploaded"
            />
          </div>

          <v-btn
            color="primary"
            :disabled="!canSend"
            :loading="sending"
            @click="sendMessage"
          >
            {{ $t('GeneralTranslations.Send') }}
          </v-btn>
        </div>
      </div>
    </v-card-actions>

    <v-card-text v-else class="text-center grey--text py-2">
      This feedback request was closed by {{ feedbackRequest.updatedByName !== feedbackRequest.familyMemberName && feedbackRequest.updatedByName ? feedbackRequest.updatedByName : 'a staff member' }}. No new messages can be sent.
    </v-card-text>

    <!-- Image Viewer Dialog -->
    <v-dialog v-model="imageDialog" max-width="800">
      <v-card>
        <v-card-text class="pa-0">
          <v-img :src="selectedImage" contain max-height="600"></v-img>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="imageDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import FeedbackMediaUploader from './FeedbackMediaUploader.vue';

export default {
  name: 'FeedbackChatPanel',
  components: {
    FeedbackMediaUploader
  },
  props: {
    feedbackRequest: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      sending: false,
      uploading: false,
      newMessage: '',
      pendingMedia: null,
      unsubscribe: null,
      imageDialog: false,
      selectedImage: ''
    };
  },
  computed: {
    ...mapGetters({
      user: 'user'
    }),
    facility() {
      return this.$store.state.authModule.currentFacilityData;
    },
    messages() {
      const storeMessages = this.$store.state.feedbackModule.feedbackMessages;
      
      // Add initial feedback details as first message if not already present
      if (this.feedbackRequest) {
        const hasInitialMessage = storeMessages.some(m => m.isInitialFeedback);
        if (!hasInitialMessage) {
          const createdAtDate = this.feedbackRequest.createdAt?.toDate 
            ? this.feedbackRequest.createdAt.toDate() 
            : this.feedbackRequest.createdAt;
          
          // Prioritize photo URL from actual messages (more up-to-date), fallback to feedbackRequest
          let photoUrl = '';
          if (storeMessages.length > 0) {
            const familyMessage = storeMessages.find(m => m.senderRole === 'family' && m.senderId === this.feedbackRequest.familyMemberId);
            if (familyMessage && familyMessage.senderPhotoUrl) {
              photoUrl = familyMessage.senderPhotoUrl;
            }
          }
          // If no photo from messages, use the one from feedbackRequest
          if (!photoUrl) {
            photoUrl = this.feedbackRequest.familyMemberPhotoUrl || '';
          }
          
          const initialMessage = {
            id: 'initial-feedback',
            isInitialFeedback: true,
            feedbackId: this.feedbackRequest.id,
            feedbackName: this.feedbackRequest.subject,
            facilityId: this.feedbackRequest.facilityId,
            orgId: this.feedbackRequest.orgId,
            residentId: this.feedbackRequest.residentId,
            senderId: this.feedbackRequest.familyMemberId,
            senderName: this.feedbackRequest.familyMemberName,
            senderPhotoUrl: photoUrl,
            senderRole: 'family',
            receiverId: null,
            text: this.feedbackRequest.details,
            medias: this.feedbackRequest.media || [],
            read: true,
            readDateTime: null,
            createdAt: this.feedbackRequest.createdAt,
            createdAtDisplay: this.formatMessageDate(createdAtDate)
          };
          return [initialMessage, ...storeMessages];
        }
      }
      return storeMessages;
    },
    isReadOnly() {
      return this.feedbackRequest.status === 'Closed';
    },
    canSend() {
      return (this.newMessage && this.newMessage.trim().length > 0) || this.pendingMedia;
    },
    attachmentPath() {
      return `${this.facility.id}/familyFeedbackMessages/media`;
    }
  },
  async mounted() {
    await this.loadMessages();
    this.subscribeToMessages();
    if (!this.isReadOnly) {
      await this.markMessagesAsRead();
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  methods: {
    async loadMessages() {
      this.loading = true;
      try {
        await this.$store.dispatch('getFeedbackMessages', this.feedbackRequest.id);
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Error loading messages:', error);
        this.$toast.open({
          message: 'Failed to load messages',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    
    subscribeToMessages() {
      this.unsubscribe = this.$store.dispatch('subscribeFeedbackMessages', {
        feedbackId: this.feedbackRequest.id,
        onMessageAdded: (message) => {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
          
          // Mark as read if from family
          if (message.senderRole === 'family' && !this.isReadOnly) {
            this.markMessagesAsRead();
          }
        }
      });
    },
    
    async markMessagesAsRead() {
      try {
        await this.$store.dispatch('markFeedbackMessagesAsRead', this.feedbackRequest.id);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    },
    
    handleFileUploaded(file) {
      this.uploading = false;
      if (file) {
        this.pendingMedia = file;
        // Auto-send if we have media
        this.sendMessage();
      }
    },
    
    async sendMessage() {
      if (!this.canSend) return;

      this.sending = true;
      try {
        const medias = this.pendingMedia ? [this.pendingMedia] : [];
        
        await this.$store.dispatch('sendFeedbackMessage', {
          feedbackId: this.feedbackRequest.id,
          text: this.newMessage.trim(),
          medias
        });

        this.newMessage = '';
        this.pendingMedia = null;

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Error sending message:', error);
        this.$toast.open({
          message: 'Failed to send message',
          type: 'error'
        });
      } finally {
        this.sending = false;
      }
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          const bottomElement = this.$refs.bottomElement;
          if (bottomElement) {
            bottomElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
          }
        }, 100);
      });
    },
    
    viewFullImage(url) {
      this.selectedImage = url;
      this.imageDialog = true;
    },
    
    formatMessageDate(date) {
      if (!date) return '';
      
      const moment = require('moment');
      const { localizedDate } = require('@/utils/localizedDate');
      
      const d = date instanceof Date ? date : new Date(date);
      
      // Use same formatting as real messages
      return moment(d).isSame(moment(), 'day')
        ? localizedDate(d, this.facility?.timeFormat)
        : localizedDate(d);
    }
  }
};
</script>

<style scoped lang="scss">
.feedback-chat-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.messages-container {
  flex: 1;
  max-height: 500px;
  overflow-y: auto;
  min-height: 300px;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 0;
}

.message-item {
  animation: fadeIn 0.3s ease-in;
  margin-bottom: 20px;
}

.message-item:last-child {
  margin-bottom: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-card-received {
  max-width: 100%;
  word-wrap: break-word;
  border-radius: 12px;
}

.message-card-sent {
  max-width: 100%;
  word-wrap: break-word;
  border-radius: 12px;
}

.message-text {
  white-space: pre-wrap;
  word-break: break-word;
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}

.cursor-pointer:hover {
  opacity: 0.8;
}
</style>
